/**
 * ★ WB — o que a bolha (e a prévia da lista) dizem sobre uma mensagem que não saiu. Puro.
 *
 * Quem REDIGE o motivo é o servidor (`functions/messaging/entrega.js`), um lugar só: só ele sabe se foi
 * recusa do provedor, chave errada ou instância ausente. Aqui se decide apenas a MOLDURA — o rótulo curto e
 * se o botão de tentar de novo aparece.
 *
 * `entrega` ausente = entregue. É o estado de tudo que já existia antes desta peça (nenhum degrau).
 */
export const FALHA = 'falha';
export const INCERTA = 'incerta';
export const REENVIANDO = 'reenviando';
/**
 * ★★★ C2 (06/out/2026) — 'pendente', o estado de PASSAGEM.
 *
 * ★ O servidor agora grava a mensagem **antes** do POST ao WhatsApp
 * (`functions/messaging/entrega.js` → `PENDENTE`), para que um timeout não a apague. ⚠ Entre o
 * `set` e a resposta do provedor ela fica aqui — normalmente milissegundos.
 *
 * ★★ Mas NÃO é só teoria: se a função morrer no meio (OOM, deploy), a bolha fica **presa** em
 * "Enviando…". ⚠⚠ Visível de propósito — **bolha presa é corrigível; bolha ausente não.**
 * ★ Sem botão de reenviar: pode ter saído.
 */
export const PENDENTE = 'pendente';

/** O rótulo curto da PRÉVIA da lista — minúsculo, ao lado do texto da mensagem (decisão do dono). */
const CURTO = {
  [FALHA]: 'não enviada',
  [INCERTA]: 'Não sabemos se chegou',
  [REENVIANDO]: 'Reenviando…',
  [PENDENTE]: 'Enviando…',
};

const estadoDe = (mensagem) => (mensagem?.direcao === 'saida' ? mensagem?.entrega ?? null : null);

/** Há algo a avisar sobre a entrega desta mensagem? */
export const temAvisoDeEntrega = (mensagem) => Boolean(CURTO[estadoDe(mensagem)]);

/**
 * O texto do aviso. `curto` (prévia da lista) traz só o estado; na bolha, o estado mais o motivo que o
 * servidor redigiu — sem ele, "Não enviada" deixa a pessoa sem saber o que fazer.
 */
export function rotuloDaEntrega(mensagem, { curto = false } = {}) {
  const estado = estadoDe(mensagem);
  const base = CURTO[estado];
  if (!base) return '';
  // ★ C2: 'pendente' e 'reenviando' não têm motivo do servidor — o rótulo curto é a mensagem toda.
  if (curto || estado === REENVIANDO || estado === PENDENTE) return base;
  // Na bolha, quem manda é o texto do servidor (título + explicação, ou o motivo antigo de uma linha).
  const erro = mensagem?.erroEnvio ?? {};
  const titulo = String(erro.titulo ?? '').trim();
  const motivo = String(erro.motivo ?? '').trim();
  if (titulo) return titulo;
  return motivo || (estado === FALHA ? 'Não enviada' : 'Não sabemos se chegou');
}

/** A segunda linha da bolha: o que fazer a respeito. Vazia quando o servidor não mandou explicação. */
export const explicacaoDaEntrega = (mensagem) => (temAvisoDeEntrega(mensagem) ? String(mensagem?.erroEnvio?.explicacao ?? '').trim() : '');

/**
 * Regra de domínio: só `falha` é reenviável — é o estado em que o servidor tem CERTEZA de que a mensagem não
 * saiu. `incerta` não ganha botão de propósito (decisão do dono): reenviar o que pode ter saído duplicaria a
 * mensagem do paciente, e quem decide isso é a pessoa, relendo a conversa.
 */
const ehReenviavel = (mensagem) => estadoDe(mensagem) === FALHA;

/**
 * ★★ WC-c1 (24/set/2026) — O BOTÃO "Tentar de novo" ESTÁ DESLIGADO, E ISTO NÃO É DECISÃO DE PRODUTO.
 *
 * O incidente do envio (`docs/wb-incidente.md`) fez a reversão das functions da WB, e a callable
 * `reenviarMensagem` DEIXOU DE EXISTIR em produção — medido: das 24 callables que esta SPA chama, 23 existem
 * e só essa não. O bundle da WB continua servido, então o botão apareceria e o clique devolveria
 * `functions/not-found`. **Tela não pode oferecer ação que o servidor não tem.**
 *
 * ★ QUEM REINTRODUZIR A CALLABLE PRECISA DEVOLVER O BOTÃO: troque esta constante para `true`, no mesmo
 * deploy em que `reenviarMensagem` voltar ao ar. A guarda de fonte
 * (`web/src/conversas/wc.guardas.test.js`, "★★ WC-c1") cobra a coerência entre esta constante e o botão na
 * Thread, e há mutação que prova que, ligada, o botão volta a aparecer — a religação é de UMA linha.
 *
 * ★ O que NÃO muda com o botão desligado: a bolha continua mostrando TÍTULO e EXPLICAÇÃO. O rastro da falha
 * — que é a razão de ser da WB — permanece inteiro; o que sai é só a ação impossível. As 7 bolhas com
 * `entrega: 'falha'` seguem no banco, intocadas.
 */
export const REENVIO_DISPONIVEL = false;

export const podeReenviar = (mensagem) => REENVIO_DISPONIVEL && ehReenviavel(mensagem);
